import { inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  WORKFLOW_FORM_FIELD_INPUT_TYPES,
  WorkflowFormErrors,
  WorkflowFormFieldValidationBase,
  WorkflowFormValidationError,
  ParsedWorkflowFieldUrl,
} from '@workflows/types';
import {
  WorkflowStorageOwnerService,
  WorkflowStorageRepository,
  workflowInstanceKeyToString,
  type WorkflowStorageOwner,
} from './storage';
import {
  getWorkflowFileOrigin,
  isWorkflowMimetypeAllowed,
} from './workflow-file-field.utils';

/**
 * Сервис для работы с ошибками валидации workflow форм
 * Отвечает за сохранение, загрузку и применение ошибок валидации к формам
 */
@Injectable({
  providedIn: 'root',
})
export class WorkflowErrorsService {
  private readonly storage = inject(WorkflowStorageRepository);
  private readonly ownerService = inject(WorkflowStorageOwnerService);

  // --------------------------------------------------------------------------
  // Методы для работы с хранилищем (с владельцем)
  // --------------------------------------------------------------------------

  /**
   * Ключ ошибок экземпляра формы
   */
  private buildWorkflowFormErrorsKey(
    requestNumber: string,
    workflowId: string,
    nodeId: number,
    formId: number,
  ): string {
    return workflowInstanceKeyToString(requestNumber, {
      requestWorkflowId: workflowId,
      nodeId,
      formId,
    });
  }

  /**
   * Сохранить ошибки валидации формы. Ошибка записи пробрасывается.
   */
  async saveFormErrors(
    requestNumber: string,
    workflowId: string,
    nodeId: number,
    formId: number,
    errors: WorkflowFormValidationError[],
    explicitOwner?: WorkflowStorageOwner,
  ): Promise<void> {
    const owner = explicitOwner ?? this.ownerService.requireOwner();
    const data: WorkflowFormErrors = {
      requestNumber,
      workflowId,
      nodeId,
      formId,
      errors,
      timestamp: Date.now(),
    };

    await this.storage.put(
      owner,
      'wf-errors',
      this.buildWorkflowFormErrorsKey(
        requestNumber,
        workflowId,
        nodeId,
        formId,
      ),
      data,
      { requestNumber },
    );
  }

  /**
   * Получить ошибки валидации формы
   */
  async getFormErrors(
    requestNumber: string,
    workflowId: string,
    nodeId: number,
    formId: number,
  ): Promise<WorkflowFormErrors | null> {
    const owner = this.ownerService.getOwner();

    if (!owner) {
      return null;
    }

    try {
      const stored = await this.storage.get<WorkflowFormErrors>(
        owner,
        'wf-errors',
        this.buildWorkflowFormErrorsKey(
          requestNumber,
          workflowId,
          nodeId,
          formId,
        ),
      );

      return stored?.value ?? null;
    } catch (error) {
      console.error('Failed to get workflow form errors', error);

      return null;
    }
  }

  /**
   * Удалить ошибки валидации формы
   */
  async removeFormErrors(
    requestNumber: string,
    workflowId: string,
    nodeId: number,
    formId: number,
    explicitOwner?: WorkflowStorageOwner,
  ): Promise<void> {
    const owner = explicitOwner ?? this.ownerService.getOwner();

    if (!owner) {
      return;
    }

    try {
      await this.storage.delete(
        owner,
        'wf-errors',
        this.buildWorkflowFormErrorsKey(
          requestNumber,
          workflowId,
          nodeId,
          formId,
        ),
      );
    } catch (error) {
      console.error('Failed to remove workflow form errors', error);
    }
  }

  /**
   * Очистить все ошибки валидации для заявки
   */
  async clearAllFormErrors(requestNumber: string): Promise<void> {
    const owner = this.ownerService.getOwner();

    if (!owner) {
      return;
    }

    try {
      const records = await this.storage.list<WorkflowFormErrors>(
        owner,
        'wf-errors',
        requestNumber,
      );

      await Promise.all(
        records.map(record =>
          this.storage.delete(owner, 'wf-errors', record.resourceKey),
        ),
      );
    } catch (error) {
      console.error('Error clearing workflow form errors', error);
    }
  }

  /**
   * Получить все ошибки для заявки (для отображения в UI)
   */
  async getAllFormErrorsForRequest(
    requestNumber: string,
  ): Promise<WorkflowFormErrors[]> {
    const owner = this.ownerService.getOwner();

    if (!owner) {
      return [];
    }

    try {
      const records = await this.storage.list<WorkflowFormErrors>(
        owner,
        'wf-errors',
        requestNumber,
      );

      return records.map(record => record.value);
    } catch (error) {
      console.error('Error getting all workflow form errors', error);

      return [];
    }
  }

  // --------------------------------------------------------------------------
  // Методы для применения ошибок к форме
  // --------------------------------------------------------------------------

  /**
   * Применить ошибки валидации к контролам формы
   *
   * Серверные ошибки полностью заменяют клиентские, т.к. они проверяют те же правила
   * из схемы формы, но более точно (с учетом серверного контекста)
   */
  applyErrorsToForm(
    errors: WorkflowFormValidationError[],
    formControls: { [key: string]: AbstractControl },
  ): void {
    errors.forEach(error => {
      const fieldKey = this.extractFieldKeyFromUrl(error.fieldUrl);
      const control = fieldKey ? (formControls[fieldKey] as FormControl) : null;

      if (control) {
        control.markAsTouched();

        // Преобразуем серверные ошибки в стандартные Angular validator errors
        const angularErrors = this.convertServerErrorsToAngularErrors(
          error.errors,
          control,
        );

        // Заменяем все ошибки серверными (не мержим с клиентскими)
        // Клиентские и серверные validators проверяют одни и те же правила
        control.setErrors(angularErrors);
      }
    });
  }

  /**
   * Извлечь fieldKey из fieldUrl
   * fieldUrl format: workflow://workflowId/nodeName/formName/fieldKey
   */
  private extractFieldKeyFromUrl(fieldUrl: string): string | null {
    if (!fieldUrl) return null;

    const lastSlashIndex = fieldUrl.lastIndexOf('/');

    return lastSlashIndex !== -1
      ? fieldUrl.substring(lastSlashIndex + 1)
      : null;
  }

  /**
   * Преобразовать серверные ошибки в стандартные Angular validator errors
   */
  private convertServerErrorsToAngularErrors(
    serverErrors: any[],
    control: FormControl,
  ): any {
    const errors: any = {};
    const value = control.value;
    const customErrors: any[] = []; // Собираем неизвестные ошибки

    serverErrors.forEach(error => {
      switch (error.validationRule) {
        case 'isRequired':
          errors.required = true;
          break;
        case 'minStringLength':
          errors.minlength = {
            requiredLength: error.ruleValue,
            actualLength:
              typeof value === 'string' ? value.length : value?.length || 0,
          };
          break;
        case 'maxStringLength':
          errors.maxlength = {
            requiredLength: error.ruleValue,
            actualLength:
              typeof value === 'string' ? value.length : value?.length || 0,
          };
          break;
        case 'minNumberValue':
          errors.min = {
            min: error.ruleValue,
            actual: typeof value === 'number' ? value : Number(value) || 0,
          };
          break;
        case 'maxNumberValue':
          errors.max = {
            max: error.ruleValue,
            actual: typeof value === 'number' ? value : Number(value) || 0,
          };
          break;
        case 'minDate':
          errors.minDate = {
            min: error.ruleValue,
            actual: value || '',
          };
          break;
        case 'maxDate':
          errors.maxDate = {
            max: error.ruleValue,
            actual: value || '',
          };
          break;
        case 'minDatetime':
          errors.minDatetime = {
            min: error.ruleValue,
            actual: value || '',
          };
          break;
        case 'maxDatetime':
          errors.maxDatetime = {
            max: error.ruleValue,
            actual: value || '',
          };
          break;
        case 'minFilesCount':
          errors.minArrayLength = {
            min: error.ruleValue,
            actual: Array.isArray(value) ? value.length : 0,
          };
          break;
        case 'maxFilesCount':
          errors.maxArrayLength = {
            max: error.ruleValue,
            actual: Array.isArray(value) ? value.length : 0,
          };
          break;
        case 'stringRegex':
          errors.pattern = {
            requiredPattern: error.ruleValue?.pattern || '',
            actualValue: value || '',
          };
          break;
        case 'allowedFilesMimetypes':
          errors.invalidMimetypes = {
            allowed: error.ruleValue,
            invalid: Array.isArray(value)
              ? value
                  .filter((f: File) => !error.ruleValue.includes(f.type))
                  .map((f: File) => ({ name: f.name, type: f.type }))
              : [],
          };
          break;
        default:
          // Все неизвестные ошибки складываем в customError
          customErrors.push({
            rule: error.validationRule,
            value: error.ruleValue,
          });
      }
    });

    // Если есть неизвестные ошибки, добавляем их как одну ошибку customError
    if (customErrors.length > 0) {
      errors.customError = customErrors;
    }

    return errors;
  }

  // --------------------------------------------------------------------------
  // Методы для построения валидаторов
  // --------------------------------------------------------------------------

  /**
   * Построить массив валидаторов на основе схемы валидации
   */
  buildValidators(
    validation: WorkflowFormFieldValidationBase | undefined,
    fieldType?: string,
  ): ValidatorFn[] {
    if (!validation) return [];

    const validators: ValidatorFn[] = [];
    // Используем any для избежания проблем с type narrowing в TypeScript
    const validationAny = validation as any;

    // Required - для чекбокса используем requiredTrue вместо required
    if (validation.isRequired) {
      if (fieldType === WORKFLOW_FORM_FIELD_INPUT_TYPES.inputCheckbox) {
        validators.push(Validators.requiredTrue);
      } else {
        validators.push(Validators.required);
      }
    }

    // String length validators
    if (
      'minStringLength' in validation &&
      typeof validationAny.minStringLength === 'number'
    ) {
      validators.push(Validators.minLength(validationAny.minStringLength));
    }
    if (
      'maxStringLength' in validation &&
      typeof validationAny.maxStringLength === 'number'
    ) {
      validators.push(Validators.maxLength(validationAny.maxStringLength));
    }

    // Number validators
    if (
      'minNumberValue' in validation &&
      typeof validationAny.minNumberValue === 'number'
    ) {
      validators.push(Validators.min(validationAny.minNumberValue));
    }
    if (
      'maxNumberValue' in validation &&
      typeof validationAny.maxNumberValue === 'number'
    ) {
      validators.push(Validators.max(validationAny.maxNumberValue));
    }

    // Date/Datetime validators (custom validators)
    if (
      'minDate' in validation &&
      validationAny.minDate &&
      typeof validationAny.minDate === 'string'
    ) {
      validators.push(this.createMinDateValidator(validationAny.minDate));
    }
    if (
      'maxDate' in validation &&
      validationAny.maxDate &&
      typeof validationAny.maxDate === 'string'
    ) {
      validators.push(this.createMaxDateValidator(validationAny.maxDate));
    }
    if (
      'minDatetime' in validation &&
      validationAny.minDatetime &&
      typeof validationAny.minDatetime === 'string'
    ) {
      validators.push(
        this.createMinDatetimeValidator(validationAny.minDatetime),
      );
    }
    if (
      'maxDatetime' in validation &&
      validationAny.maxDatetime &&
      typeof validationAny.maxDatetime === 'string'
    ) {
      validators.push(
        this.createMaxDatetimeValidator(validationAny.maxDatetime),
      );
    }

    // File count validators
    if (
      'minFilesCount' in validation &&
      typeof validationAny.minFilesCount === 'number'
    ) {
      validators.push(
        this.createMinArrayLengthValidator(validationAny.minFilesCount),
      );
    }
    if (
      'maxFilesCount' in validation &&
      typeof validationAny.maxFilesCount === 'number'
    ) {
      validators.push(
        this.createMaxArrayLengthValidator(validationAny.maxFilesCount),
      );
    }

    // String regex validator
    if (
      'stringRegex' in validation &&
      validationAny.stringRegex &&
      typeof validationAny.stringRegex === 'object' &&
      'pattern' in validationAny.stringRegex &&
      typeof validationAny.stringRegex.pattern === 'string'
    ) {
      // Шаблон приходит из схемы регламента и backend его не проверяет:
      // невалидный regex не должен ронять построение формы
      try {
        const pattern = new RegExp(validationAny.stringRegex.pattern);

        validators.push(Validators.pattern(pattern));
      } catch (error) {
        console.error(
          'Invalid stringRegex pattern in workflow schema, validator skipped',
          validationAny.stringRegex.pattern,
          error,
        );
      }
    }

    // File mimetypes validator
    if (
      'allowedFilesMimetypes' in validation &&
      Array.isArray(validationAny.allowedFilesMimetypes)
    ) {
      validators.push(
        this.createMimetypeValidator(validationAny.allowedFilesMimetypes),
      );
    }

    return validators;
  }

  /**
   * Создать валидатор минимальной даты
   */
  private createMinDateValidator(minDate: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null;

      const value = new Date(control.value);
      const min = new Date(minDate);

      return value < min
        ? { minDate: { min: minDate, actual: control.value } }
        : null;
    };
  }

  /**
   * Создать валидатор максимальной даты
   */
  private createMaxDateValidator(maxDate: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null;

      const value = new Date(control.value);
      const max = new Date(maxDate);

      return value > max
        ? { maxDate: { max: maxDate, actual: control.value } }
        : null;
    };
  }

  /**
   * Создать валидатор минимальной даты и времени
   */
  private createMinDatetimeValidator(minDatetime: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null;

      const value = new Date(control.value);
      const min = new Date(minDatetime);

      return value < min
        ? { minDatetime: { min: minDatetime, actual: control.value } }
        : null;
    };
  }

  /**
   * Создать валидатор максимальной даты и времени
   */
  private createMaxDatetimeValidator(maxDatetime: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null;

      const value = new Date(control.value);
      const max = new Date(maxDatetime);

      return value > max
        ? { maxDatetime: { max: maxDatetime, actual: control.value } }
        : null;
    };
  }

  /**
   * Создать валидатор минимальной длины массива
   */
  private createMinArrayLengthValidator(min: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null;

      const length = Array.isArray(control.value) ? control.value.length : 0;

      return length < min ? { minArrayLength: { min, actual: length } } : null;
    };
  }

  /**
   * Создать валидатор максимальной длины массива
   */
  private createMaxArrayLengthValidator(max: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null;

      const length = Array.isArray(control.value) ? control.value.length : 0;

      return length > max ? { maxArrayLength: { max, actual: length } } : null;
    };
  }

  /**
   * Создать валидатор для проверки MIME типов файлов
   */
  private createMimetypeValidator(allowedMimetypes: string[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!Array.isArray(control.value) || allowedMimetypes.length === 0) {
        return null;
      }

      // Файлы с сервера уже прошли проверку backend; у фото с сервера нет File
      const invalidFiles = control.value
        .map((item: unknown) => getValidatableFile(item))
        .filter(
          (file): file is File =>
            file !== null &&
            !isWorkflowMimetypeAllowed(file.type, allowedMimetypes),
        );

      return invalidFiles.length > 0
        ? {
            invalidMimetypes: {
              allowed: allowedMimetypes,
              invalid: invalidFiles.map(file => ({
                name: file.name,
                type: file.type,
              })),
            },
          }
        : null;
    };
  }

  // --------------------------------------------------------------------------
  // Методы для работы с ошибками финальной страницы
  // --------------------------------------------------------------------------

  /**
   * Парсит fieldUrl из ответа сервера
   * Формат (backend buildWorkflowSchema): workflow://<workflowId>/<nodeKey>/<formKey>/<fieldKey>
   * Ключи узла и формы приходят как есть, без префиксов node_/form_
   */
  parseFieldUrl(fieldUrl: string): ParsedWorkflowFieldUrl | null {
    const pattern = /^workflow:\/\/([^/]+)\/([^/]+)\/([^/]+)\/(.+)$/;
    const match = fieldUrl?.match(pattern);

    if (!match) return null;

    return {
      workflowId: match[1],
      nodeKey: match[2],
      formKey: match[3],
      fieldKey: match[4],
    };
  }

  /**
   * Маппинг правила валидации в человекочитаемое сообщение
   */
  getValidationErrorMessage(validationRule: string, ruleValue?: any): string {
    const messages: Record<string, string> = {
      isRequired: 'Обязательное поле',
      requiredIf: 'Поле обязательно при выполнении условия',
      minDate: `Минимальная дата: ${ruleValue}`,
      maxDate: `Максимальная дата: ${ruleValue}`,
      minDatetime: `Минимальная дата и время: ${ruleValue}`,
      maxDatetime: `Максимальная дата и время: ${ruleValue}`,
      minStringLength: `Минимальная длина: ${ruleValue}`,
      maxStringLength: `Максимальная длина: ${ruleValue}`,
      minNumberValue: `Минимальное значение: ${ruleValue}`,
      maxNumberValue: `Максимальное значение: ${ruleValue}`,
      stringRegex: 'Неверный формат',
      allowedFilesMimetypes: 'Недопустимый тип файла',
      minLength: `Минимальная длина: ${ruleValue}`,
      maxLength: `Максимальная длина: ${ruleValue}`,
      minValue: `Минимальное значение: ${ruleValue}`,
      maxValue: `Максимальное значение: ${ruleValue}`,
      pattern: 'Неверный формат',
      minFilesCount: `Минимум файлов: ${ruleValue}`,
      maxFilesCount: `Максимум файлов: ${ruleValue}`,
      minArrayLength: `Минимум элементов: ${ruleValue}`,
      maxArrayLength: `Максимум элементов: ${ruleValue}`,
    };

    return messages[validationRule] || `Ошибка валидации: ${validationRule}`;
  }
}

/**
 * Файл значения поля, который нужно проверить по MIME: новый File поля inputFiles
 * или локальный файл фото (FileItem). Файлы, уже сохранённые на сервере, не проверяются.
 */
function getValidatableFile(item: unknown): File | null {
  if (item instanceof File) {
    return getWorkflowFileOrigin(item)?.kind === 'server' ? null : item;
  }

  if (typeof item === 'object' && item !== null && 'file' in item) {
    const file = (item as { file?: unknown }).file;

    return file instanceof File ? file : null;
  }

  return null;
}
